import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, Menu, Phone, X } from "lucide-react";
import { Logo } from "./Logo";
import { useContent } from "../context/ContentContext";

const barLink =
  "inline-block px-4 py-4 font-display text-sm font-bold uppercase tracking-wide text-white/85 transition-colors duration-200 hover:bg-white/10 hover:text-white";

export const Header = () => {
  const { services, site } = useContent();
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setDropOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <header data-testid="site-header">
      <div className="bg-white">
        <div className="mx-auto max-w-[1400px] px-4 py-5 sm:px-6">
          <Link to="/" data-testid="header-logo-link" aria-label="DT Timmerwerken, naar home" className="inline-block">
            <Logo />
          </Link>
        </div>
      </div>

      <div className="sticky top-0 z-50 bg-[#111111]">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 sm:px-6">
          <nav className="hidden items-center lg:flex" aria-label="Hoofdnavigatie">
            <NavLink to="/" data-testid="nav-home-link" className={barLink}>
              Home
            </NavLink>
            <div
              className="relative"
              onMouseEnter={() => setDropOpen(true)}
              onMouseLeave={() => setDropOpen(false)}
            >
              <button
                type="button"
                data-testid="nav-diensten-dropdown"
                className={`${barLink} flex items-center gap-1`}
                onClick={() => setDropOpen((v) => !v)}
                aria-expanded={dropOpen}
              >
                Diensten
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${dropOpen ? "rotate-180" : ""}`} />
              </button>
              {dropOpen && (
                <div className="absolute left-0 top-full z-50 w-60 border-t-2 border-cream bg-white shadow-[0_16px_40px_-16px_rgba(0,0,0,0.35)]">
                  {services.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/diensten/${s.slug}`}
                      data-testid={`nav-dienst-${s.slug}`}
                      className="group flex items-center justify-between px-4 py-3 font-display text-sm font-bold uppercase tracking-wide text-[#333333] transition-colors duration-200 hover:bg-cream-soft"
                    >
                      {s.title}
                      <ChevronRight className="h-4 w-4 opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <NavLink to="/portfolio" data-testid="nav-portfolio-link" className={barLink}>
              Portfolio
            </NavLink>
            <NavLink to="/over-mij" data-testid="nav-over-mij-link" className={barLink}>
              Over mij
            </NavLink>
            <NavLink to="/contact" data-testid="nav-contact-link" className={barLink}>
              Contact
            </NavLink>
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a
              href={site.phoneHref}
              data-testid="header-phone-link"
              aria-label={`Bel ${site.phoneDisplay}`}
              className="flex h-9 w-9 items-center justify-center text-white/85 transition-colors duration-200 hover:text-white"
            >
              <Phone className="h-4 w-4" />
            </a>
            <Link
              to="/contact"
              data-testid="header-offerte-button"
              className="my-2.5 inline-block rounded-[4px] bg-cream px-5 py-2 font-display text-sm font-bold uppercase tracking-wide text-[#111111] transition-colors duration-200 hover:bg-cream-deep"
            >
              Offerte aanvragen
            </Link>
          </div>

          <div className="flex w-full items-center justify-between lg:hidden">
            <span className="py-3 font-display text-sm font-bold uppercase tracking-wide text-white">
              Menu
            </span>
            <div className="flex items-center gap-2">
              <Link
                to="/contact"
                data-testid="header-offerte-button-mobile"
                className="rounded-[4px] bg-cream px-4 py-2 font-display text-xs font-bold uppercase tracking-wide text-[#111111]"
              >
                Offerte
              </Link>
              <button
                type="button"
                data-testid="mobile-menu-button"
                onClick={() => setMobileOpen((v) => !v)}
                aria-label={mobileOpen ? "Menu sluiten" : "Menu openen"}
                className="flex h-10 w-10 items-center justify-center text-white"
              >
                {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <div data-testid="mobile-menu" className="border-t border-white/10 bg-[#111111] lg:hidden">
            <nav className="mx-auto max-w-[1400px] px-4 py-4 sm:px-6" aria-label="Mobiel menu">
              {[
                { to: "/", label: "Home", testid: "mobile-nav-home" },
                { to: "/portfolio", label: "Portfolio", testid: "mobile-nav-portfolio" },
                { to: "/over-mij", label: "Over mij", testid: "mobile-nav-over-mij" },
                { to: "/contact", label: "Contact", testid: "mobile-nav-contact" },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className="block border-b border-white/10 py-3.5 font-display text-lg font-bold uppercase tracking-wide text-white"
                >
                  {item.label}
                </Link>
              ))}
              <p className="pt-5 font-display text-xs font-bold uppercase tracking-[0.2em] text-white/50">
                Diensten
              </p>
              <div className="grid grid-cols-2 gap-x-6">
                {services.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/diensten/${s.slug}`}
                    data-testid={`mobile-nav-dienst-${s.slug}`}
                    className="border-b border-white/10 py-3 font-display text-sm font-bold uppercase tracking-wide text-white/80"
                  >
                    {s.title}
                  </Link>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-4 pt-6">
                <a
                  href={site.phoneHref}
                  data-testid="mobile-menu-phone"
                  className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-white"
                >
                  <Phone className="h-4 w-4" /> {site.phoneDisplay}
                </a>
                <Link
                  to="/contact"
                  data-testid="mobile-menu-offerte"
                  className="rounded-[4px] bg-cream px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wide text-[#111111]"
                >
                  Offerte aanvragen
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
